import { useEffect, useRef, useState, useCallback } from 'react'
import './image.scss'

// Nota: todo el contenido de cada ref está https://www.industrialempathy.com/posts/image-optimizations/
const Image = props => {
	const {
		type = 'image/svg',
		src,
		testId,
		alt,
		loading = 'lazy', // Por defecto lazy
		width,
		height = 'auto',
		ratio = '1/1',
		decoding = 'async',
		classes,
		fit = 'cover',
		onClick,
		priority = false, // Nueva prop para imágenes críticas
		eager = false // Nueva prop para forzar carga eagern
	} = props

	// constants
	const [isDataImg, setIsDataImg] = useState({
		visible: false,
		loaded: false,
		error: false
	})
	const skeletonHeight = height === 'auto' ? width : height
	// ref element
	const imgRef = useRef(null)
	const observerRef = useRef(null)

	// Determinar estrategia de carga
	const shouldUseEagerLoading = priority || eager || loading === 'eager'
	const actualLoading = shouldUseEagerLoading ? 'eager' : 'lazy'

	// actions
	const onIntersect = useCallback(async ([{ isIntersecting }], observerElement) => {
		if (isIntersecting) {
			setIsDataImg(prev => ({
				...prev,
				visible: true
			}))

			// Cleanup observer after image becomes visible
			if (imgRef.current && observerElement) {
				await observerElement.unobserve(imgRef.current)
				observerRef.current = null
			}
		}
	}, [])

	const onLoad = useCallback(() => {
		setIsDataImg(prev => ({
			...prev,
			loaded: true
		}))
	}, [])

	const onError = useCallback(() => {
		setIsDataImg(prev => ({
			...prev,
			error: true,
			loaded: true // Evitar que siga intentando cargar
		}))
	}, [])

	// Intersection Observer para lazy loading mejorado
	useEffect(() => {
		// Si es eager loading, marcar como visible inmediatamente
		if (shouldUseEagerLoading) {
			setIsDataImg(prev => ({ ...prev, visible: true }))
			return
		}

		// Solo usar Intersection Observer para lazy loading
		if (!imgRef.current || observerRef.current) return

		const observer = new IntersectionObserver(onIntersect, {
			rootMargin: '50px', // Comenzar a cargar 50px antes de ser visible
			threshold: 0.1
		})

		observer.observe(imgRef.current)
		observerRef.current = observer

		return () => {
			if (observerRef.current && imgRef.current) {
				observerRef.current.unobserve(imgRef.current)
			}
			observerRef.current = null
		}
	}, [onIntersect, shouldUseEagerLoading])

	return (
		<picture
			ref={imgRef}
			data-testid={testId}
			className={`a-image ${classes || ''} ${priority ? 'a-image--priority' : ''}`}
			style={{
				width: `${width}px`,
				minWidth: `${width}px`,
				height: !isDataImg.loaded ? `${skeletonHeight}px` : `${height}${height !== 'auto' && 'px'}`
			}}
			onClick={() => onClick && onClick()}
		>
			<source sizes={`(max-width: ${width}px) 100vw, ${width}px`} srcSet={src} type={type} />

			{/* Skeleton loader mientras la imagen no está cargada */}
			{!isDataImg.loaded && !isDataImg.error && (
				<div
					className='a-image__skeleton'
					style={{
						width: width ? `${width}px` : '100%',
						height: `${skeletonHeight || 100}px`
					}}
				/>
			)}

			{/* Solo renderizar la imagen cuando sea visible en el viewport o sea priority */}
			{isDataImg.visible && (
				<img
					src={src}
					alt={alt}
					width={width}
					height={height}
					loading={actualLoading}
					decoding={decoding}
					data-testid={testId}
					onLoad={onLoad}
					onError={onError}
					style={{
						width: `${width}px`,
						maxWidth: `${width}px`,
						aspectRatio: ratio,
						objectFit: fit,
						display: isDataImg.error ? 'none' : 'block'
					}}
				/>
			)}

			{/* Fallback en caso de error */}
			{isDataImg.error && (
				<div
					className='a-image__error'
					style={{
						width: width ? `${width}px` : '100%',
						height: `${skeletonHeight || 100}px`,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						backgroundColor: '#f0f0f0',
						color: '#666'
					}}
				>
					Error al cargar imagen
				</div>
			)}
		</picture>
	)
}

export default Image
